import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import AIAssistant from "@/components/AIAssistant";
import VoiceAssistant from "@/components/VoiceAssistant";
import { woocommerceAPI } from "@/lib/api";
import {
  LayoutDashboard,
  Package,
  List,
  ShoppingCart,
  TrendingUp,
  BarChart3,
  Calculator,
  Settings,
  LogOut,
  Menu,
  X,
  Truck,
  Radio,
  Plug,
  Home,
  Droplets,
  Tv,
  Tag,
  Users,
  CreditCard,
  PackageCheck,
  ClipboardList,
  Shield,
  Clock,
  ScanLine,
} from "lucide-react";

const navItems = [
  { path: "/home", icon: Home, label: "Registra Ordine" },
  { path: "/riempimento", icon: Droplets, label: "Registro Riempimento" },
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/sales", icon: ShoppingCart, label: "Archivio Vendite" },
  { path: "/tempo-reale", icon: Radio, label: "Tempo Reale" },
  { path: "/live", icon: Tv, label: "Live" },
  { path: "/transito", icon: Truck, label: "Transito" },
  { path: "/integrazioni", icon: Plug, label: "Integrazione" },
  { path: "/spedizioni", icon: PackageCheck, label: "Spedizioni" },
  { path: "/scanner", icon: ScanLine, label: "Scanner e Spedizioni" },
  { path: "/etichette-gls", icon: Truck, label: "Etichette GLS" },
  { path: "/picking-list", icon: ClipboardList, label: "Picking List" },
  { path: "/sds-generator", icon: Shield, label: "Generatore SDS" },
  { path: "/perfume-list", icon: List, label: "Elenco Profumi" },
  { path: "/inventory", icon: Package, label: "Inventario" },
  { path: "/stampa-etichette", icon: Tag, label: "Stampa Etichette" },
  { path: "/clienti", icon: Users, label: "Database Clienti" },
  { path: "/corrispettivi", icon: CreditCard, label: "Corrispettivi" },
  { path: "/revenue", icon: TrendingUp, label: "Fatturato" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/scorporo", icon: Calculator, label: "Scorporo" },
  { path: "/settings", icon: Settings, label: "Impostazioni" },
];

const DashboardLayout = () => {
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [lastSyncCount, setLastSyncCount] = useState(0);

  // Auto-sync WooCommerce orders every 5 seconds
  useEffect(() => {
    const syncOrders = async () => {
      try {
        const res = await woocommerceAPI.sync();
        if (res.data?.imported > 0) {
          setLastSyncCount(prev => prev + res.data.imported);
          // Trigger a custom event to notify other components
          window.dispatchEvent(new CustomEvent('woocommerce-sync', { detail: { imported: res.data.imported } }));
        }
      } catch (error) {
        // Silent fail for background sync
      }
    };

    // Initial sync
    syncOrders();

    // Set up interval for every 5 seconds
    const interval = setInterval(syncOrders, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Dark */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-72 sidebar-dark transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-8 border-b border-white/10">
            <h1 className="font-serif text-2xl text-white tracking-wide">
              Equivalencia
            </h1>
            <p className="text-[11px] text-[#C9A227] tracking-[0.3em] uppercase mt-1 font-medium">
              Equipe
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `nav-item flex items-center gap-3 px-4 py-3.5 rounded-lg transition-all duration-200 ${
                    isActive ? "active" : ""
                  }`
                }
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-medium">
                  {item.label}
                </span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-6 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-[#C9A227]/20 flex items-center justify-center">
                <span className="text-[#C9A227] text-base font-semibold">
                  {user?.name?.charAt(0).toUpperCase() || "E"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{user?.name || "Team"}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="ghost"
              className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg text-sm font-medium"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 bg-white shadow-sm px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
              className="p-2 text-gray-600 hover:text-[#C9A227] rounded-lg"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-2">
              <span className="font-serif text-xl text-gray-900">Equivalencia</span>
            </div>
            <div className="w-10" />
          </div>
        </header>

        {/* Page content */}
        <main className={`flex-1 p-6 md:p-8 lg:p-10 overflow-auto ${pathname === "/" ? "bg-[#0A0B0F]" : ""}`}>
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* AI Assistant */}
      {pathname !== "/scanner" && <AIAssistant />}

      {/* Custom badge to cover "Made with Emergent" */}
      <div 
        className="fixed bottom-4 right-4 bg-[#1A1A1A] text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium border border-[#C9A227]/30"
        style={{ minWidth: '190px', zIndex: 2147483647 }}
      >
        <div className="w-6 h-6 rounded-full bg-[#C9A227] flex items-center justify-center text-black font-bold text-xs">
          E
        </div>
        <span>Equivalencia Team</span>
      </div>
    </div>
  );
};

export default DashboardLayout;
